import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useContextSelector } from "use-context-selector";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import AuthContext from "../context/AuthContext";
import { apiPost } from "../Utils/apiFetch";
import { apiPath } from "../Utils/apiPath";
import faeye from "../fa-eye.svg";
import faeyeslash from "../fa-eye-slash.svg";
import "./ChangePassword.css";

const passwordFields = [
  { name: "newPassword", label: "New Password", autoComplete: "new-password" },
  {
    name: "confirmPassword",
    label: "Confirm Password",
    autoComplete: "new-password",
  },
  {
    name: "oldPassword",
    label: "Your Password",
    autoComplete: "current-password",
  },
];

export default function ChangePassword() {
  const navigate = useNavigate();
  const setUser = useContextSelector(AuthContext, (ctx) => ctx.setUser);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [requestError, setRequestError] = useState("");
  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { newPassword: "", confirmPassword: "", oldPassword: "" },
  });
  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    if (confirmPassword) trigger("confirmPassword");
  }, [newPassword, confirmPassword, trigger]);

  const submitPassword = async ({ oldPassword, newPassword }) => {
    setRequestError("");
    try {
      const { status, data } = await apiPost(apiPath.changePassword, {
        oldPassword,
        newPassword,
        uniqueId: Math.random() * 10000,
      });
      if (status !== 200 || !data?.success) {
        setRequestError(
          data?.message || "Unable to change your password. Please try again.",
        );
        return;
      }
      if (data.results?.token) {
        const updatedUser = jwtDecode(data.results.token);
        localStorage.setItem("token", data.results.token);
        if (data.results.refresh_token) {
          localStorage.setItem("refresh_token", data.results.refresh_token);
        }
        setUser(updatedUser);
      }
      toast.success(data.message || "Password changed successfully.");
      navigate("/", { replace: true });
    } catch (error) {
      setRequestError(
        error.response?.data?.message ||
          "Unable to change your password. Please try again.",
      );
    }
  };

  const Field = isMobile ? "dd" : "div";
  const fields = passwordFields.map(({ name, label, autoComplete }) => (
    <Field className="password-group" key={name}>
      <label className="change-password-label" htmlFor={name}>
        {label}
      </label>
      <div className="change-password-input">
        <input
          id={name}
          type={visiblePasswords[name] ? "text" : "password"}
          placeholder={label}
          className={
            isMobile ? undefined : "form-control ng-dirty ng-valid ng-touched"
          }
          autoComplete={autoComplete}
          maxLength={16}
          disabled={isSubmitting}
          aria-invalid={!!errors[name]}
          aria-describedby={errors[name] ? `${name}-error` : undefined}
          {...register(name, {
            required:
              name === "confirmPassword"
                ? "Please confirm your new password."
                : `Please enter ${name === "oldPassword" ? "your" : "a new"} password.`,
            minLength: {
              value: 8,
              message: "Password must be at least 8 characters.",
            },
            maxLength: {
              value: 16,
              message: "Password cannot exceed 16 characters.",
            },
            validate: (value) => {
              if (!value.trim()) return "White spaces are not allowed.";
              if (name === "confirmPassword" && value !== newPassword) {
                return "New password and confirm password do not match.";
              }
              return true;
            },
          })}
        />
        <button
          type="button"
          className="change-password-toggle"
          aria-label={`${visiblePasswords[name] ? "Hide" : "Show"} ${label.toLowerCase()}`}
          aria-pressed={!!visiblePasswords[name]}
          onClick={() =>
            setVisiblePasswords((current) => ({
              ...current,
              [name]: !current[name],
            }))
          }
        >
          <img src={visiblePasswords[name] ? faeye : faeyeslash} alt="" />
        </button>
      </div>
      {errors[name] && (
        <span id={`${name}-error`} className="error-text" role="alert">
          {errors[name].message}
        </span>
      )}
    </Field>
  ));

  const errorMessage = requestError && (
    <p className="error-text" role="alert">
      {requestError}
    </p>
  );
  const submitButton = (
    <button
      type="submit"
      className={isMobile ? "btn-send ui-link" : "btn-send"}
      disabled={isSubmitting}
    >
      {isSubmitting ? "Changing..." : "Change Password"}
    </button>
  );
  const passwordForm = (
    <form
      noValidate
      className={
        isMobile ? "change-password-form" : "login-panel change-password-form"
      }
      onSubmit={handleSubmit(submitPassword)}
      aria-busy={isSubmitting}
    >
      {isMobile ? (
        <dl className="form-login">
          <dt className="change-password-title">Change Password</dt>
          {fields}
          <dd>{errorMessage}</dd>
          <dd>{submitButton}</dd>
        </dl>
      ) : (
        <>
          <div className="header black-text">Change Password</div>
          {fields}
          {errorMessage}
          {submitButton}
        </>
      )}
    </form>
  );

  return (
    <>
      {isSubmitting && (
        <div className="over-wrap loading-overlay ng-star-inserted">
          <div
            id="loading"
            className="loading-wrap"
            style={{ display: "block" }}
          >
            <ul className="loading">
              <li>
                <img
                  src="/assets/images/loading40.gif"
                  className="loaderclass"
                  alt=""
                />
              </li>
              <li style={{ padding: "10px" }}>Loading...</li>
            </ul>
          </div>
        </div>
      )}
      {isMobile ? (
        <HelmetProvider>
          <Helmet>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
          </Helmet>
          <app-login>
            <app-login-mob>
              <div className="mobile-view change-password-page">
                <div className="ui-mobile-viewport ui-overlay-a">
                  <div
                    data-role="page"
                    className="ui-page ui-page-theme-a ui-page-active backhround"
                  >
                    <header style={{ display: "block" }} className="login-head">
                      <h1>SKYEXCHANGE</h1>
                    </header>
                    <div className="apk-btn">{passwordForm}</div>
                  </div>
                </div>
              </div>
            </app-login-mob>
          </app-login>
        </HelmetProvider>
      ) : (
        <div className="dynamicBack ng-star-inserted change-password-page">
          <div className="scene" />
          <div className="login-wrap">
            <div className="kv">
              <img src="/assets/images/transparent.gif" alt="Logo" />
            </div>
            {passwordForm}
          </div>
        </div>
      )}
    </>
  );
}
